import React from "react";
import PropTypes from "prop-types";
import { NavLink } from "react-router-dom";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import locationimg from "../assets/images/icons/location-01.svg";
import mailimg from "../assets/images/icons/mail.svg";
import callingimg from "../assets/images/icons/calling.svg";
import gstLogo from "../assets/images/logo/gstb_logo.svg";
import playstore from "../assets/images/home/playstore.svg";
import appstore from "../assets/images/home/appstore.svg";

const SocialIcon = ({ children, label, href }) => (
  <a
    className="btn social-btn me-2"
    href={href || "#"}
    aria-label={label}
    target={href ? "_blank" : undefined}
    rel={href ? "noopener noreferrer" : undefined}
  >
    {children}
  </a>
);

SocialIcon.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  href: PropTypes.string,
};

const Footer = ({
  year = new Date().getFullYear(),
  quickLinks = [],
  usefulLinks = [],
}) => {
  // default link sets (if not provided as props)
  const defaultQuick = [
    { to: "/", label: "Home" },
    { to: "/membership", label: "Membership" },
    { to: "/events", label: "Events" },
    { to: "/events-gallery", label: "Events Gallery" },
    { to: "/sponsor", label: "Sponsors" },
    { to: "/committee", label: "Meet Our Teams" },
  ];

  const defaultUseful = [
    { to: "/rsvp", label: "RSVP" },
    { to: "/about-us", label: "About Us" },
    { to: "/bylaws", label: "ByLaws", target: "_blank" },
    { to: "/privacy-policy", label: "Privacy Policy" },
    { to: "/terms-and-conditions", label: "Terms & Conditions" },
    { to: "/faqs", label: "FAQs" },
  ];

  const qLinks = quickLinks.length ? quickLinks : defaultQuick;
  const uLinks = usefulLinks.length ? usefulLinks : defaultUseful;

  return (
    <footer className="site-footer bg-white text-dark">
      <div className="container py-4">
        <div className="footer-card p-xl-4 p-3">
          <div className="row gy-4 px-2">
            {/* Brand */}
            <div className="col-12 col-md-12 col-lg-8 col-xl-4 d-flex flex-column gap-2">
              <div className="d-flex align-items-start flex-column">
                <img
                  src={gstLogo}
                  alt="GSTB Logo"
                  className="brand-logo mb-2"
                />
                <div>
                  <p className="mb-0 small brand-desc">
                    Gujarati Samaj of Tampa Bay Inc (GSTB) is a vibrant cultural
                    organization dedicated to preserving and promoting Gujarati
                    heritage, culture, and traditions in the Tampa Bay area.
                  </p>
                </div>
              </div>

              <div className="mt-2 d-flex align-items-center">
                <SocialIcon
                  label="facebook"
                  href="https://www.facebook.com/GujaratiSamajofTampaBay"
                >
                  <FaFacebookF size={20} />
                </SocialIcon>
                {/* <SocialIcon label="twitter" href="https://twitter.com">
                  <FaTwitter size={20} />
                </SocialIcon> */}
                <SocialIcon
                  label="instagram"
                  href="https://www.instagram.com/gujaratisamajtampa"
                >
                  <FaInstagram size={20} />
                </SocialIcon>
                {/* <SocialIcon label="linkedin" href="https://linkedin.com">
                  <FaLinkedinIn size={20} />
                </SocialIcon> */}
              </div>
              {/* App Store & Play Store Row */}
              <div className="mt-3 d-flex align-items-center gap-3">
                <a
                  href="https://play.google.com/store/apps/details?id=com.gstb.app"
                  target="_blank"
                  rel="play store"
                >
                  <img
                    src={playstore}
                    alt="Get it on Google Play"
                    height="40"
                    style={{ cursor: "pointer" }}
                  />
                </a>
                <a
                  href="https://apps.apple.com/in/app/gstb/id6757752046"
                  target="_blank"
                  rel="app store"
                >
                  <img
                    src={appstore}
                    alt="Download on App Store"
                    height="40"
                    style={{ cursor: "pointer" }}
                  />
                </a>
              </div>
            </div>

            {/* Links */}
            <div className="col-12 col-md-12 col-lg-12 col-xl-8 mt-5">
              <div className="row d-flex justify-content-lg-center justify-content-start">
                <nav
                  className="col-6 col-md-3 col-lg-3"
                  aria-label="Quick links"
                >
                  <h5 className="inter-semibold">Quick Links</h5>
                  <ul className="list-unstyled footer-links mt-2">
                    {qLinks.map((l) => (
                      <li key={l.to}>
                        <NavLink
                          to={l.to}
                          className={({ isActive }) =>
                            isActive ? "active nav-link" : "nav-link"
                          }
                          target={l.target}
                          onClick={(e) => {
                            if (l.disable) {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          }}
                          style={
                            l.disable
                              ? { opacity: 0.5, cursor: "not-allowed" }
                              : {}
                          }
                        >
                          {l.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </nav>

                <nav
                  className="col-6 col-md-3 col-lg-3"
                  aria-label="Useful links"
                >
                  <h5 className="inter-semibold">Useful Links</h5>
                  <ul className="list-unstyled footer-links mt-3">
                    {uLinks.map((l) => (
                      <li key={l.to}>
                        <NavLink
                          to={l.to}
                          className={({ isActive }) =>
                            isActive ? "active nav-link" : "nav-link"
                          }
                          target={l.target}
                          onClick={(e) => {
                            if (l.disable) {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          }}
                          style={
                            l.disable
                              ? { opacity: 0.5, cursor: "not-allowed" }
                              : {}
                          }
                        >
                          {l.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </nav>

                {/* Contact */}
                <address className="col-12 col-md-6 col-lg-6 col-xl-5 contact-col">
                  <h5 className="inter-semibold">Contact Us</h5>
                  {/* <h5 className="inter-semibold">Help</h5> */}

                  <ul className="list-unstyled contact-list mt-3">
                    {/* Location */}
                    <li className="d-flex align-items-center mb-3">
                      {/* <IoLocationOutline
                        size={20}
                        className="me-2 contact-icon"
                      /> */}
                      <img src={locationimg} alt="calendar" className="me-2" />
                      <span>5513 Lynn Road, Tampa, FL 33624</span>
                    </li>

                    {/* Email */}
                    <li className="d-flex align-items-center mb-3">
                      <img src={mailimg} alt="calendar" className="me-2" />
                      <a
                        href="mailto:gujaratisamajoftampabay@gmail.com"
                        className="text-decoration-none"
                      >
                        gujaratisamajoftampabay@gmail.com
                      </a>
                    </li>

                    {/* Phone */}
                    <li className="d-flex align-items-center mb-2">
                      <img src={callingimg} alt="calendar" className="me-2" />
                      <a
                        href="tel:+1 (865) 771-9190"
                        className="text-decoration-none"
                      >
                        +1 (865) 771-9190
                      </a>
                    </li>
                    {/* <li className="d-flex align-items-center mb-2">
                      <img src={callingimg} alt="calendar" className="me-2" />
                      <a
                        href=""
                        className="text-decoration-none"
                      >
                        FAQs
                      </a>
                    </li> */}
                  </ul>
                </address>
              </div>
            </div>
          </div>
        </div>

        {/* bottom row */}
        <div className="footer-bottom mt-3">
          <p className="mb-0 small text-center text-muted">
            Copyright © {year} Gujarati Samaj of Tampa Bay Inc. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  year: PropTypes.number,
  quickLinks: PropTypes.array,
  usefulLinks: PropTypes.array,
};

export default Footer;
